import { useCallback, useEffect } from 'react';
import type { PropsWithChildren } from 'react';
import { Inline, Box } from '@stitch-fix/mode-react';
import { useFeatureFlags } from '@stitch-fix/sf-next';
import { removeSeeItOnMeLook } from '@stitch-fix/vision-shared';
import { useScreenViewEvent } from '@stitch-fix/event-reporter';
import { useRouter } from 'next/router';
import LookFeedback from '../LookFeedback';
import { useGetVisionLookQuery } from './graphql/getVisionLook';
import LoadingScreen from './LoadingScreen';
import { Look } from '../../../../types/definitions/vendor/graphql-schema.d';
import { sanitizeReturnUrl } from '../../../../utils/routingHelper';
import { clearGalleryRestorationReturn, markGalleryRestorationReturn } from '../../gallery/hooks/useGalleryRestoration';
type LookContentProps = {
  lookId?: string; // optional prop for Storybook/testing
};
const LookContentScreenView = ({
  lookId,
  children
}: PropsWithChildren<{
  lookId: string;
}>) => {
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'screen_view',
    name: 'vision_look_details',
    source_app: 'categories-next-ui',
    content_id: lookId
  });
  return <EventProvider>{children}</EventProvider>;
};
const LookContent = ({
  lookId: propLookId
}: LookContentProps) => {
  const router = useRouter();
  const lookId = propLookId ?? router.query.lookId;
  const returnUrl = sanitizeReturnUrl(router.query.returnUrl);
  const restorationReturnUrl = sanitizeReturnUrl(router.query.returnUrl, '');
  const {
    featureFlags
  } = useFeatureFlags('client', ['client.vision.share_url_enabled']);
  const isShareUrlEnabled = !!featureFlags?.['client.vision.share_url_enabled'];
  const {
    data,
    loading,
    error
  } = useGetVisionLookQuery({
    variables: {
      lookId: lookId as string,
      requestOrigin: 'FREESTYLE_CATEGORIES'
    },
    skip: !lookId
  });
  const look = data?.client?.vision?.look as Look | undefined;
  const returnToGallery = useCallback(() => {
    const markedForRestoration = markGalleryRestorationReturn(restorationReturnUrl);
    return Promise.resolve(router.push(returnUrl)).then(didNavigate => {
      if (markedForRestoration && !didNavigate) {
        clearGalleryRestorationReturn();
      }
      return didNavigate;
    }, navigationError => {
      if (markedForRestoration) clearGalleryRestorationReturn();
      throw navigationError;
    });
  }, [restorationReturnUrl, returnUrl, router]);
  useEffect(() => {
    if (!router.isReady) return;
    if (lookId && !loading && (error || !look)) {
      returnToGallery();
    }
  }, [router.isReady, lookId, loading, error, look, returnToGallery]);
  const handleOnComplete = () => returnToGallery();
  if (typeof lookId !== 'string') return null;
  const content = <Inline spacing={1} hAlign="center" pt={{
    sm: 0,
    lg: 3
  }}>
      <Box maxWidth="900px">
        {loading && <LoadingScreen />}
        {look && <LookFeedback look={look} shoppableItems={look.shopProductVariants} isShareUrlEnabled={isShareUrlEnabled} onComplete={handleOnComplete} onDelete={() => {
        removeSeeItOnMeLook(look.id);
        returnToGallery();
      }} />}
      </Box>
    </Inline>;
  return <LookContentScreenView lookId={lookId}>{content}</LookContentScreenView>;
};
export default LookContent;